import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useProject } from '@contexts/Project.context';
import { invalidateCollectionResults } from '@utils/collectionQueries';

import useSetSearchParams from '@hooks/useSetSearchParams.hook';

/**
 * Bring a collection's detection results onto the map.
 *
 * The collection a detection ran for isn't necessarily the one the map is
 * showing, so both halves are needed: select it, *and* refresh the cache.
 * Invalidating alone refreshes entries no mounted component observes and changes
 * nothing on screen; selecting alone leaves stale results in place.
 *
 * Shared by every flow that lands on existing-or-just-produced results — the
 * Object Picker wizard's "already analyzed" outcome and the completion of a
 * detection job — so they all select a collection the same way.
 *
 * @returns {(collectionUuid: string | null | undefined) => Promise<void>}
 *   Resolves once the invalidated queries that were already cached have settled.
 */
export const useShowCollectionResults = () => {
	const { dispatch } = useProject();
	const setSearchParams = useSetSearchParams();
	const queryClient = useQueryClient();

	return useCallback(
		collectionUuid => {
			// Selected the same way the Analyzed Collections picker does: the URL is
			// the source of truth and context mirrors it. Both are written in the
			// same tick on purpose — CollectionPicker's URL→context sync resets a
			// uuid it can't find in its (still refetching) list, and it only reaches
			// that check when the URL and context disagree.
			if (collectionUuid) {
				dispatch({ type: 'setCollectionUuid', payload: collectionUuid });
				setSearchParams({ collection_uuid: collectionUuid });
			}

			// When the selection just changed, the newly observed query keys fetch on
			// their own and this covers the shared ones (the collections list). When
			// the collection was already selected, this is what pulls in the new
			// results instead of waiting out each query's staleTime.
			return invalidateCollectionResults(queryClient);
		},
		[dispatch, setSearchParams, queryClient]
	);
};
